/**
 * AnalyticsLoader — fetches enabled analytics providers from the backend and
 * injects their script tags into <head>. Fail-silent.
 *
 * Features:
 *  - Per-provider pageRules (mode: all / include / exclude, patterns with glob wildcards).
 *  - Reacts to route changes (useLocation) so rules are re-evaluated on SPA navigation.
 *    Scripts that were loaded once stay loaded (analytics doesn't tear down cleanly),
 *    but scripts that weren't injected yet get injected when the user navigates
 *    to a matching page.
 *  - Verification meta tags (GSC/Bing/Yandex) bypass rules — they must be global.
 *  - GDPR: tracking scripts only inject after cookie consent analytics:true.
 *    Verification meta tags load regardless (no tracking).
 *
 * Providers:
 *  google_analytics | google_tag_manager | google_search_console | bing_webmaster
 *  yandex_metrika | yandex_webmaster | facebook_pixel | tiktok_pixel
 *  hotjar | plausible | umami | clarity
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchPublicAnalyticsConfig } from "../../services/seoAuditService";
import { shouldLoadOnPage } from "../../utils/pageRules";

const INJECTED_FLAG = "data-pb-analytics-loader";

function getConsentState() {
  // Returns { analytics, marketing } — defaults to denied if no cookie yet.
  if (typeof document === "undefined") return { analytics: false, marketing: false };
  const match = document.cookie.match(/(?:^|;\s*)pb_cookie_consent=([^;]+)/);
  if (!match) return { analytics: false, marketing: false };
  try {
    const obj = JSON.parse(decodeURIComponent(match[1]));
    return { analytics: obj?.analytics === true, marketing: obj?.marketing === true };
  } catch {
    return { analytics: false, marketing: false };
  }
}

function injectScript({ id, src, inline, async = true, defer = false }) {
  if (typeof document === "undefined") return null;
  if (id && document.getElementById(id)) return null;
  const s = document.createElement("script");
  if (id) s.id = id;
  if (src) s.src = src;
  if (inline) s.textContent = inline;
  if (async) s.async = true;
  if (defer) s.defer = true;
  s.setAttribute(INJECTED_FLAG, "1");
  document.head.appendChild(s);
  return s;
}

function injectMeta({ name, content, id }) {
  if (typeof document === "undefined" || !content) return null;
  if (id && document.getElementById(id)) return null;
  const m = document.createElement("meta");
  if (id) m.id = id;
  m.setAttribute("name", name);
  m.setAttribute("content", content);
  m.setAttribute(INJECTED_FLAG, "1");
  document.head.appendChild(m);
  return m;
}

// ─── Provider loaders ────────────────────────────────────────────────────────

/**
 * Initialize Google Consent Mode v2 before any gtag call.
 * Defaults all storage to "denied" — required for GDPR.
 * Real tracking activates when consent cookie has analytics:true.
 * Must be injected BEFORE any GTM/GA4 src tag.
 */
function ensureConsentMode(consent) {
  if (typeof document === "undefined") return;
  if (document.getElementById("gtag-consent-default")) return;
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  injectScript({
    id: "gtag-consent-default",
    async: false,
    inline: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  'ad_storage': '${marketing}',
  'ad_user_data': '${marketing}',
  'ad_personalization': '${marketing}',
  'analytics_storage': '${analytics}',
  'functionality_storage': 'granted',
  'security_storage': 'granted',
  'wait_for_update': 500
});
gtag('set', 'url_passthrough', true);
gtag('set', 'ads_data_redaction', true);`,
  });
}

/**
 * Update consent state after user clicks Accept in the cookie banner.
 * Call gtag('consent','update', ...) to unblock tracking.
 */
function updateConsentMode(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
  });
}

function loadGA4(measurementId) {
  if (!measurementId) return;
  // Script tag loads unconditionally so Google tag verification can see it.
  // Consent Mode v2 (ensureConsentMode) controls whether data is actually sent.
  injectScript({
    id: "ga4-src",
    src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
  });
  injectScript({
    id: "ga4-init",
    inline: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${measurementId}', { send_page_view: true });`,
  });
}

function loadGTM(containerId) {
  if (!containerId) return;
  injectScript({
    id: "gtm-init",
    inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${containerId}');`,
  });
}

function loadYandexMetrika(counterId) {
  if (!counterId) return;
  injectScript({
    id: "ym-init",
    inline: `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
m[i].l=1*new Date();for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
(window, document,'script','https://mc.yandex.ru/metrika/tag.js','ym');
ym(${Number(counterId)}, 'init', {clickmap:true, trackLinks:true, accurateTrackBounce:true});`,
  });
}

function loadFacebookPixel(pixelId) {
  if (!pixelId) return;
  injectScript({
    id: "fbq-init",
    inline: `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');`,
  });
}

function loadTikTokPixel(pixelId) {
  if (!pixelId) return;
  injectScript({
    id: "ttq-init",
    inline: `!function (w, d, t) {
w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
ttq.load('${pixelId}');
ttq.page();
}(window, document, 'ttq');`,
  });
}

function loadHotjar(siteId) {
  if (!siteId) return;
  injectScript({
    id: "hj-init",
    inline: `(function(h,o,t,j,a,r){
h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
h._hjSettings={hjid:${Number(siteId)},hjsv:6};
a=o.getElementsByTagName('head')[0];
r=o.createElement('script');r.async=1;
r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
a.appendChild(r);
})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
  });
}

function loadPlausible(domain, scriptUrl) {
  if (!domain || typeof document === "undefined") return;
  if (document.getElementById("plausible-src")) return;
  const s = document.createElement("script");
  s.id = "plausible-src";
  s.defer = true;
  s.setAttribute("data-domain", domain);
  s.src = scriptUrl || "https://plausible.io/js/script.js";
  s.setAttribute(INJECTED_FLAG, "1");
  document.head.appendChild(s);
}

function loadUmami(websiteId, scriptUrl) {
  if (!websiteId || !scriptUrl || typeof document === "undefined") return;
  if (document.getElementById("umami-src")) return;
  const s = document.createElement("script");
  s.id = "umami-src";
  s.defer = true;
  s.setAttribute("data-website-id", websiteId);
  s.src = scriptUrl;
  s.setAttribute(INJECTED_FLAG, "1");
  document.head.appendChild(s);
}

function loadClarity(projectId) {
  if (!projectId) return;
  injectScript({
    id: "clarity-init",
    inline: `(function(c,l,a,r,i,t,y){
c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "${projectId}");`,
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AnalyticsLoader() {
  const { pathname } = useLocation();
  const [config, setConfig] = useState(null);
  const [verifInjected, setVerifInjected] = useState(false);

  // Fetch config once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicAnalyticsConfig();
        if (!cancelled) setConfig(data || {});
      } catch {
        if (!cancelled) setConfig({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Inject verification meta tags once (global, not per-page)
  useEffect(() => {
    if (!config || verifInjected) return;
    if (config.google_search_console?.verification) {
      injectMeta({
        id: "gsc-verify",
        name: "google-site-verification",
        content: config.google_search_console.verification,
      });
    }
    if (config.bing_webmaster?.verification) {
      injectMeta({ id: "bing-verify", name: "msvalidate.01", content: config.bing_webmaster.verification });
    }
    if (config.yandex_webmaster?.verification) {
      injectMeta({ id: "yandex-verify", name: "yandex-verification", content: config.yandex_webmaster.verification });
    }
    setVerifInjected(true);
  }, [config, verifInjected]);

  // Inject tracking scripts based on pageRules + current pathname.
  // Re-runs on every navigation; loader functions are idempotent.
  //
  // Strategy:
  //   - Google (GA4/GTM) and Plausible/Umami use Consent Mode v2 → loaded ALWAYS
  //     when enabled, so tag verification works. Data is gated by consent state.
  //   - Facebook/TikTok/Hotjar/Clarity/Yandex.Metrika do NOT have consent APIs
  //     that work without extra config, so we only inject them after consent.
  useEffect(() => {
    if (!config) return;

    const consent = getConsentState();
    const shouldLoad = (provider) => {
      const p = config[provider];
      if (!p?.enabled) return false;
      return shouldLoadOnPage(pathname, p.pageRules);
    };

    // 1) Consent Mode v2 default — must run before any gtag script
    const needsGoogle =
      shouldLoad("google_tag_manager") || shouldLoad("google_analytics");
    if (needsGoogle) {
      ensureConsentMode(consent);
    }

    // 2) Google tags — load ALWAYS (consent handled by gtag consent API)
    if (shouldLoad("google_tag_manager")) loadGTM(config.google_tag_manager.containerId);
    if (shouldLoad("google_analytics")) loadGA4(config.google_analytics.measurementId);

    // 3) Cookieless analytics — safe to load always
    if (shouldLoad("plausible"))
      loadPlausible(config.plausible.domain, config.plausible.scriptUrl);
    if (shouldLoad("umami"))
      loadUmami(config.umami.websiteId, config.umami.scriptUrl);

    // 4) Consent-required providers (cookie-based tracking without gtag API)
    if (consent.analytics) {
      if (shouldLoad("yandex_metrika")) loadYandexMetrika(config.yandex_metrika.counterId);
      if (shouldLoad("hotjar")) loadHotjar(config.hotjar.siteId);
      if (shouldLoad("clarity")) loadClarity(config.clarity.projectId);
    }
    if (consent.marketing) {
      if (shouldLoad("facebook_pixel")) loadFacebookPixel(config.facebook_pixel.pixelId);
      if (shouldLoad("tiktok_pixel")) loadTikTokPixel(config.tiktok_pixel.pixelId);
    }

    // 5) If user already consented, push update to gtag (unlocks GA4/GTM data collection)
    if (needsGoogle && (consent.analytics || consent.marketing)) {
      updateConsentMode(consent);
    }
  }, [config, pathname]);

  // Re-evaluate on consent changes (user clicks Accept/Decline in banner).
  // CookieConsent dispatches "pb:consent:changed" custom event AND calls gtag
  // directly for Google. Here we also inject non-Google trackers (FB Pixel,
  // Hotjar, etc.) that weren't loaded before because consent wasn't given.
  useEffect(() => {
    if (!config) return;

    const handler = () => {
      const consent = getConsentState();
      // Push gtag update (idempotent — CookieConsent already did this)
      if (config.google_analytics?.enabled || config.google_tag_manager?.enabled) {
        updateConsentMode(consent);
      }
      // Inject consent-gated providers now that user agreed
      const shouldLoadNow = (provider) => {
        const p = config[provider];
        if (!p?.enabled) return false;
        return shouldLoadOnPage(pathname, p.pageRules);
      };
      if (consent.analytics) {
        if (shouldLoadNow("yandex_metrika")) loadYandexMetrika(config.yandex_metrika.counterId);
        if (shouldLoadNow("hotjar")) loadHotjar(config.hotjar.siteId);
        if (shouldLoadNow("clarity")) loadClarity(config.clarity.projectId);
      }
      if (consent.marketing) {
        if (shouldLoadNow("facebook_pixel")) loadFacebookPixel(config.facebook_pixel.pixelId);
        if (shouldLoadNow("tiktok_pixel")) loadTikTokPixel(config.tiktok_pixel.pixelId);
      }
    };

    window.addEventListener("pb:consent:changed", handler);
    return () => window.removeEventListener("pb:consent:changed", handler);
  }, [config, pathname]);

  return null;
}
